import { UUIDType } from "./index";

/**
 * Subscription phases representing different enforcement levels
 */
export enum SubscriptionPhase {
  Active = "Active",
  Awareness = "Awareness",
  Grace = "Grace",
  Soft_Enforcement = "Soft_Enforcement",
  Compliance_Archive = "Compliance_Archive"
}

/**
 * Numeric phase levels for comparison and enforcement checks
 * Higher values indicate more severe subscription status
 */
export enum SubscriptionPhaseLevel {
  Active = 0,
  Awareness = 1,
  Grace = 2,
  Soft_Enforcement = 3,
  Compliance_Archive = 4
}

/**
 * Enforcement configuration for a specific level
 */
export interface EnforcementConfigType {
  level: number;
  bannerColor: string;
  bannerDismissible: boolean;
  turnstileEnabled: boolean;
  /**
   * @deprecated No longer used - turnstile modal no longer displays countdown timer.
   * Kept for backward compatibility with existing database records.
   */
  turnstileDurationSeconds: number | null;
  /**
   * @deprecated No longer used - turnstile modal now shows only once per session after facility selection.
   * Kept for backward compatibility with existing database records.
   */
  turnstileFrequencyMinutes: number | null;
  // Additional runtime enforcement settings
  showTurnstile?: boolean;
  /**
   * @deprecated Never used in implementation. Kept for potential future use.
   */
  turnstileDelaySeconds?: number;
}

/**
 * Result of calculating subscription phase for a facility
 */
export interface SubscriptionPhaseResult {
  facilityId: UUIDType;
  phase: SubscriptionPhase;
  daysUntilExpiry: number;
  daysAfterExpiry: number;
  enforcementConfig: EnforcementConfigType;
}

/**
 * Status details for a single facility
 */
export interface FacilityStatusDetail {
  facilityId: UUIDType;
  facilityName?: string;
  name?: string; // Alias for facilityName for convenience
  phase: SubscriptionPhase;
  daysRemaining: number;
  enforcementConfig?: EnforcementConfigType;
}

/**
 * Phase transition details
 */
export interface PhaseTransition {
  facilityId: UUIDType;
  facilityName: string;
  oldPhase: SubscriptionPhase;
  newPhase: SubscriptionPhase;
  transitionDate: Date;
  requiresNotification: boolean;
  businessSpocEmails: string[];
}

/**
 * Maps phase to numeric level for comparison
 * Returns SubscriptionPhaseLevel value (0-4)
 */
export function getPhaseLevel(phase: SubscriptionPhase): SubscriptionPhaseLevel {
  switch (phase) {
    case SubscriptionPhase.Active:
      return SubscriptionPhaseLevel.Active;
    case SubscriptionPhase.Awareness:
      return SubscriptionPhaseLevel.Awareness;
    case SubscriptionPhase.Grace:
      return SubscriptionPhaseLevel.Grace;
    case SubscriptionPhase.Soft_Enforcement:
      return SubscriptionPhaseLevel.Soft_Enforcement;
    case SubscriptionPhase.Compliance_Archive:
      return SubscriptionPhaseLevel.Compliance_Archive;
    default:
      return SubscriptionPhaseLevel.Active;
  }
}

/**
 * Formats a date for display using US locale (e.g., "January 15, 2025")
 * Used consistently across server-side email services and API responses
 */
export function formatDisplayDate(date: Date | string | null | undefined): string {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

/**
 * Formats phase name for display (replaces underscores with spaces)
 * Used consistently across server and client for human-readable phase names
 */
export function formatPhaseName(phase: SubscriptionPhase): string {
  switch (phase) {
    case SubscriptionPhase.Active:
      return "Active";
    case SubscriptionPhase.Awareness:
      return "Awareness";
    case SubscriptionPhase.Grace:
      return "Grace";
    case SubscriptionPhase.Soft_Enforcement:
      return "Soft Enforcement";
    case SubscriptionPhase.Compliance_Archive:
      return "Compliance Archive";
    default:
      return "Unknown";
  }
}
